import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    IconButton,
    Link,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/PageHeader';

/**
 * Create Invoice — the invoice page for the entries ticked on Outstanding
 * Invoicing. A port of the legacy client screen invoicing_form_create.php: the
 * company and client blocks, Date / Due Date / Reference, one editable line per
 * timesheet entry (description, qty, unit price, account, tax rate), Add a new
 * line, and the running totals, then Send To Xero as a draft.
 *
 * The invoice number is Xero's: legacy showed a guessed next number but never
 * sent it. The totals here are a preview; Xero computes the real ones, and the
 * copy saved to the client records Xero's figures.
 */

interface DraftLine {
    readonly timesheetEntryId: number | null;
    readonly description: string;
    readonly quantity: number | null;
    readonly unitAmount: number | null;
    readonly accountCode: string | null;
    readonly taxType: string | null;
}

interface InvoiceDraft {
    readonly client: {
        readonly id: number;
        readonly name: string;
        readonly addressLines: readonly string[];
        readonly linkedToXero: boolean;
    };
    readonly company: { readonly name: string; readonly addressLines: readonly string[]; readonly abn: string | null };
    readonly date: string;
    readonly dueDate: string | null;
    readonly reference: string;
    readonly lineAmountTypes: 'Exclusive' | 'Inclusive' | 'NoTax';
    readonly lines: readonly DraftLine[];
    readonly accounts: readonly { readonly code: string; readonly name: string; readonly taxType: string | null }[];
    readonly accountsError: string | null;
    readonly taxTypes: readonly { readonly code: string; readonly label: string }[];
    readonly xeroCanCreate: boolean;
    readonly xeroMessage: string | null;
    readonly xeroNeedsReconnect: boolean;
}

interface Created {
    readonly invoice: { readonly id: string; readonly number: string | null; readonly total: number };
    readonly clientId: number;
    readonly entriesInvoiced: number;
    readonly copySaved: boolean;
    readonly warning: string | null;
}

/** A line as edited: numbers stay text until sent, so "1." can be typed. */
interface EditLine {
    readonly key: number;
    readonly timesheetEntryId: number | null;
    readonly description: string;
    readonly quantity: string;
    readonly unitAmount: string;
    readonly accountCode: string;
    readonly taxType: string;
}

const AMOUNTS_ARE: Record<InvoiceDraft['lineAmountTypes'], string> = {
    Exclusive: 'Tax Exclusive',
    Inclusive: 'Tax Inclusive',
    NoTax: 'No Tax',
};

/** Australian GST rates by Xero tax type; anything else carries none. */
const GST_RATE: Record<string, number> = {
    OUTPUT: 0.1,
    INPUT: 0.1,
    CAPEXINPUT: 0.1,
    GSTONIMPORTS: 0.1,
    GSTONCAPIMPORTS: 0.1,
};

function money(v: number): string {
    const abs = Math.abs(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${v < 0 ? '-' : ''}$${abs}`;
}

function num(v: string): number | null {
    if (v.trim() === '') {
        return null;
    }
    const n = Number(v.replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
}

let nextKey = 1;

function toEdit(l: DraftLine): EditLine {
    return {
        key: nextKey++,
        timesheetEntryId: l.timesheetEntryId,
        description: l.description,
        quantity: l.quantity === null ? '' : String(l.quantity),
        unitAmount: l.unitAmount === null ? '' : l.unitAmount.toFixed(2),
        accountCode: l.accountCode ?? '',
        taxType: l.taxType ?? '',
    };
}

/** A line with neither quantity nor price is a heading or note. */
function isPriced(l: EditLine): boolean {
    return l.quantity.trim() !== '' || l.unitAmount.trim() !== '';
}

function lineProblem(l: EditLine): string | null {
    if (l.description.trim() === '') {
        return 'needs a description';
    }
    if (!isPriced(l)) {
        return null;
    }
    if (num(l.quantity) === null) {
        return 'needs a quantity';
    }
    if (num(l.unitAmount) === null) {
        return 'needs a unit price';
    }
    if (l.accountCode.trim() === '') {
        return 'needs an account';
    }
    if (l.taxType === '') {
        return 'needs a tax rate';
    }
    return null;
}

export function OutstandingInvoiceCreate(): React.JSX.Element {
    const navigate = useNavigate();
    const { me } = useAuth();
    const [connecting, setConnecting] = useState(false);
    const [params] = useSearchParams();
    const idsParam = params.get('ids') ?? '';
    const [draft, setDraft] = useState<InvoiceDraft | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [date, setDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [reference, setReference] = useState('');
    const [lines, setLines] = useState<readonly EditLine[]>([]);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [created, setCreated] = useState<Created | null>(null);

    useEffect(() => {
        let cancelled = false;
        apiFetch<InvoiceDraft>(`/outstanding-invoicing/invoice-draft?ids=${encodeURIComponent(idsParam)}`)
            .then((d) => {
                if (!cancelled) {
                    setDraft(d);
                    setDate(d.date);
                    setDueDate(d.dueDate ?? '');
                    setReference(d.reference);
                    setLines(d.lines.map(toEdit));
                    setLoadError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setLoadError(err instanceof Error ? err.message : 'Could not build the invoice');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [idsParam]);

    const totals = useMemo(() => {
        let subTotal = 0;
        let gst = 0;
        for (const l of lines) {
            const amount = (num(l.quantity) ?? 0) * (num(l.unitAmount) ?? 0);
            const rate = GST_RATE[l.taxType] ?? 0;
            subTotal += amount;
            if (draft?.lineAmountTypes === 'Exclusive') {
                gst += amount * rate;
            } else if (draft?.lineAmountTypes === 'Inclusive') {
                gst += (amount * rate) / (1 + rate);
            }
        }
        const total = draft?.lineAmountTypes === 'Exclusive' ? subTotal + gst : subTotal;
        return { subTotal, gst, total };
    }, [lines, draft?.lineAmountTypes]);

    /** Xero's consent screen, asking for the current scopes. It returns to Clients afterwards. */
    function reconnect(): void {
        setConnecting(true);
        apiFetch<{ url: string }>('/xero/connect', { method: 'POST' })
            .then((r) => {
                window.location.assign(r.url);
            })
            .catch((err: unknown) => {
                setSendError(err instanceof Error ? err.message : 'Could not start the Xero connection');
                setConnecting(false);
            });
    }

    function back(): void {
        // Up one path segment: /outstanding-invoicing, or the dev preview's copy of it.
        void navigate('..', { relative: 'path' });
    }

    function update(key: number, patch: Partial<EditLine>): void {
        setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    }

    function changeAccount(key: number, code: string): void {
        // As legacy changeAccount(): the account's own tax type comes with it.
        const taxType = draft?.accounts.find((a) => a.code === code)?.taxType;
        update(key, taxType !== null && taxType !== undefined ? { accountCode: code, taxType } : { accountCode: code });
    }

    function addLine(): void {
        const last = lines[lines.length - 1];
        setLines((prev) => [
            ...prev,
            {
                key: nextKey++,
                timesheetEntryId: null,
                description: '',
                quantity: '',
                unitAmount: '',
                accountCode: last?.accountCode ?? '',
                taxType: last?.taxType ?? '',
            },
        ]);
    }

    const problems = lines
        .map((l, i) => {
            const p = lineProblem(l);
            return p === null ? null : `Line ${String(i + 1)} ${p}`;
        })
        .filter((p): p is string => p !== null);
    const formProblem =
        date === ''
            ? 'The invoice needs a date'
            : dueDate === ''
              ? 'The invoice needs a due date'
              : dueDate < date
                ? 'The due date is before the invoice date'
                : !lines.some(isPriced)
                  ? 'The invoice has no priced lines'
                  : (problems[0] ?? null);
    const removedEntries =
        draft === null ? 0 : draft.lines.length - lines.filter((l) => l.timesheetEntryId !== null).length;

    function send(): void {
        if (draft === null) {
            return;
        }
        setSending(true);
        setSendError(null);
        apiFetch<Created>('/outstanding-invoicing/invoices', {
            method: 'POST',
            body: JSON.stringify({
                // Every ticked entry is marked invoiced, even one whose line was deleted
                // (legacy did the same): it was chosen to go on this invoice.
                entryIds: draft.lines.map((l) => l.timesheetEntryId).filter((id): id is number => id !== null),
                date,
                dueDate,
                reference: reference.trim() === '' ? null : reference.trim(),
                lines: lines.map((l) => ({
                    timesheetEntryId: l.timesheetEntryId,
                    description: l.description.trim(),
                    quantity: num(l.quantity),
                    unitAmount: num(l.unitAmount),
                    accountCode: l.accountCode.trim() === '' ? null : l.accountCode.trim(),
                    taxType: l.taxType === '' ? null : l.taxType,
                })),
            }),
        })
            .then((r) => {
                setCreated(r);
                window.scrollTo({ top: 0 });
            })
            .catch((err: unknown) => {
                setSendError(err instanceof Error ? err.message : 'Could not send the invoice to Xero');
            })
            .finally(() => {
                setSending(false);
            });
    }

    if (created !== null) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <PageHeader title="Invoice Sent" subtitle={draft?.client.name ?? ''} onBackClick={back} />
                <Paper sx={{ p: 3 }}>
                    <Stack gap={2}>
                        <Alert severity="success">
                            Draft invoice {created.invoice.number ?? ''} ({money(created.invoice.total)}) created in
                            Xero for {draft?.client.name}. {created.entriesInvoiced}{' '}
                            {created.entriesInvoiced === 1 ? 'entry' : 'entries'} marked invoiced.
                            {created.copySaved ? ' A copy is saved to the client’s invoices.' : ''}
                        </Alert>
                        {created.warning !== null ? <Alert severity="warning">{created.warning}</Alert> : null}
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <Button
                                variant="contained"
                                endIcon={<OpenInNewIcon />}
                                href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${created.invoice.id}`}
                                target="_blank"
                                rel="noopener"
                            >
                                Open Draft in Xero
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    void navigate(`/clients?client=${String(created.clientId)}&tab=invoicing`);
                                }}
                            >
                                View Client Invoices
                            </Button>
                            <Button onClick={back}>Back to Outstanding Invoicing</Button>
                        </Stack>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader
                title="Create Invoice"
                subtitle={draft?.client.name ?? 'Outstanding Invoicing'}
                onBackClick={back}
            />

            {loadError !== null ? (
                <Alert severity="error" action={<Button onClick={back}>Back</Button>}>
                    {loadError}
                </Alert>
            ) : null}

            {draft === null ? (
                <Paper sx={{ minHeight: 480 }} />
            ) : (
                <Paper sx={{ p: { xs: 2, md: 4 } }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                        <Box
                            component="img"
                            src="/silverline-logo.png"
                            alt=""
                            sx={{ height: 90, maxWidth: 240, objectFit: 'contain' }}
                        />
                        <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} gap={1}>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Invoice
                            </Typography>
                            <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                Number assigned by Xero
                            </Typography>
                        </Stack>
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={3}>
                        <Box>
                            <Typography sx={{ fontWeight: 700 }}>{draft.client.name}</Typography>
                            {draft.client.addressLines.map((l) => (
                                <Typography key={l}>{l}</Typography>
                            ))}
                        </Box>
                        <Box sx={{ textAlign: { sm: 'right' } }}>
                            <Typography sx={{ fontWeight: 700 }}>{draft.company.name}</Typography>
                            {draft.company.addressLines.map((l) => (
                                <Typography key={l}>{l}</Typography>
                            ))}
                            {draft.company.abn !== null ? <Typography>ABN: {draft.company.abn}</Typography> : null}
                        </Box>
                    </Stack>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            mt: 4,
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: '170px 170px 1fr auto' },
                            alignItems: 'center',
                        }}
                    >
                        <TextField
                            size="small"
                            type="date"
                            label="Date"
                            value={date}
                            onChange={(e) => {
                                setDate(e.target.value);
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="Due Date"
                            value={dueDate}
                            onChange={(e) => {
                                setDueDate(e.target.value);
                            }}
                            error={dueDate === ''}
                            helperText={
                                dueDate === '' && draft.dueDate === null
                                    ? 'The client has no due-date period set'
                                    : undefined
                            }
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            size="small"
                            label="Reference - Job"
                            value={reference}
                            onChange={(e) => {
                                setReference(e.target.value);
                            }}
                            slotProps={{ htmlInput: { maxLength: 255 } }}
                        />
                        <Chip variant="outlined" label={`Amounts are ${AMOUNTS_ARE[draft.lineAmountTypes]}`} />
                    </Box>

                    {draft.xeroMessage !== null ? (
                        <Alert
                            severity="warning"
                            sx={{ mt: 3 }}
                            action={
                                draft.xeroNeedsReconnect && me?.isSuperAdmin === true ? (
                                    <Button
                                        color="inherit"
                                        variant="outlined"
                                        size="small"
                                        disabled={connecting}
                                        onClick={reconnect}
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Reconnect Xero
                                    </Button>
                                ) : undefined
                            }
                        >
                            {draft.xeroMessage} The invoice can be checked here but not sent until then.
                            {draft.xeroNeedsReconnect && me?.isSuperAdmin === true
                                ? ' Xero brings you back to Clients afterwards; come back here and tick the entries again.'
                                : ''}
                        </Alert>
                    ) : null}
                    {draft.accountsError !== null && draft.xeroMessage === null ? (
                        <Alert severity="info" sx={{ mt: 3 }}>
                            {draft.accountsError} Type the account code instead.
                        </Alert>
                    ) : null}

                    <TableContainer
                        sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}
                    >
                        <Table size="small" sx={{ minWidth: 1000 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Description</TableCell>
                                    <TableCell sx={{ width: 100 }} align="right">
                                        Qty
                                    </TableCell>
                                    <TableCell sx={{ width: 120 }} align="right">
                                        Unit Price
                                    </TableCell>
                                    <TableCell sx={{ width: 220 }}>Account</TableCell>
                                    <TableCell sx={{ width: 190 }}>Tax Rate</TableCell>
                                    <TableCell sx={{ width: 110 }} align="right">
                                        Amount
                                    </TableCell>
                                    <TableCell sx={{ width: 48 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lines.map((l) => {
                                    const priced = isPriced(l);
                                    const amount = (num(l.quantity) ?? 0) * (num(l.unitAmount) ?? 0);
                                    return (
                                        <TableRow key={l.key} sx={{ verticalAlign: 'top' }}>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    multiline
                                                    minRows={1}
                                                    value={l.description}
                                                    error={l.description.trim() === ''}
                                                    onChange={(e) => {
                                                        update(l.key, { description: e.target.value });
                                                    }}
                                                    slotProps={{
                                                        htmlInput: { maxLength: 4000, style: { fontSize: '0.85rem' } },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    value={l.quantity}
                                                    error={priced && num(l.quantity) === null}
                                                    onChange={(e) => {
                                                        update(l.key, { quantity: e.target.value });
                                                    }}
                                                    slotProps={{
                                                        htmlInput: {
                                                            inputMode: 'decimal',
                                                            style: { textAlign: 'right' },
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    value={l.unitAmount}
                                                    error={priced && num(l.unitAmount) === null}
                                                    placeholder={priced ? 'No rate' : ''}
                                                    onChange={(e) => {
                                                        update(l.key, { unitAmount: e.target.value });
                                                    }}
                                                    onBlur={() => {
                                                        const n = num(l.unitAmount);
                                                        if (n !== null) {
                                                            update(l.key, { unitAmount: n.toFixed(2) });
                                                        }
                                                    }}
                                                    slotProps={{
                                                        htmlInput: {
                                                            inputMode: 'decimal',
                                                            style: { textAlign: 'right' },
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {draft.accounts.length > 0 ? (
                                                    <TextField
                                                        select
                                                        size="small"
                                                        fullWidth
                                                        value={l.accountCode}
                                                        error={priced && l.accountCode === ''}
                                                        onChange={(e) => {
                                                            changeAccount(l.key, e.target.value);
                                                        }}
                                                    >
                                                        {/* Keep a client default that is not in the list selectable rather than blank. */}
                                                        {l.accountCode !== '' &&
                                                        !draft.accounts.some((a) => a.code === l.accountCode) ? (
                                                            <MenuItem value={l.accountCode}>{l.accountCode}</MenuItem>
                                                        ) : null}
                                                        {draft.accounts.map((a) => (
                                                            <MenuItem key={a.code} value={a.code}>
                                                                {a.name} - {a.code}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                ) : (
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        placeholder="Account code"
                                                        value={l.accountCode}
                                                        error={priced && l.accountCode.trim() === ''}
                                                        onChange={(e) => {
                                                            update(l.key, { accountCode: e.target.value });
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    select
                                                    size="small"
                                                    fullWidth
                                                    value={l.taxType}
                                                    error={priced && l.taxType === ''}
                                                    onChange={(e) => {
                                                        update(l.key, { taxType: e.target.value });
                                                    }}
                                                >
                                                    {l.taxType !== '' &&
                                                    !draft.taxTypes.some((t) => t.code === l.taxType) ? (
                                                        <MenuItem value={l.taxType}>{l.taxType}</MenuItem>
                                                    ) : null}
                                                    {draft.taxTypes.map((t) => (
                                                        <MenuItem key={t.code} value={t.code}>
                                                            {t.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </TableCell>
                                            <TableCell align="right" sx={{ pt: 2, whiteSpace: 'nowrap' }}>
                                                {priced ? money(amount) : ''}
                                            </TableCell>
                                            <TableCell sx={{ pt: 1.25 }}>
                                                <Tooltip title="Remove line">
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Remove line"
                                                        onClick={() => {
                                                            setLines((prev) => prev.filter((x) => x.key !== l.key));
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addLine} sx={{ mt: 1.5 }}>
                        Add a new line
                    </Button>

                    <Stack alignItems="flex-end" gap={0.5} sx={{ mt: 3 }}>
                        <Typography>Subtotal {money(totals.subTotal)}</Typography>
                        <Typography>
                            {draft.lineAmountTypes === 'Inclusive' ? 'Includes GST' : 'GST'} {money(totals.gst)}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                            Total {money(totals.total)}
                        </Typography>
                        <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Xero calculates the final figures.
                        </Typography>
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    {removedEntries > 0 ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {removedEntries} {removedEntries === 1 ? 'entry’s line has' : 'entries’ lines have'} been
                            removed. {removedEntries === 1 ? 'It' : 'They'} will still be marked invoiced when this is
                            sent. Go back and untick {removedEntries === 1 ? 'it' : 'them'} to leave{' '}
                            {removedEntries === 1 ? 'it' : 'them'} outstanding.
                        </Alert>
                    ) : null}
                    {sendError !== null ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {sendError}
                        </Alert>
                    ) : null}

                    <Stack
                        direction={{ xs: 'column-reverse', sm: 'row' }}
                        justifyContent="flex-end"
                        alignItems={{ sm: 'center' }}
                        gap={1.5}
                    >
                        {formProblem !== null ? (
                            <Typography color="warning.main" sx={{ fontSize: '0.875rem', mr: { sm: 'auto' } }}>
                                {formProblem}
                            </Typography>
                        ) : null}
                        <Button onClick={back} disabled={sending}>
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<SendIcon />}
                            disabled={!draft.xeroCanCreate || formProblem !== null || sending}
                            onClick={send}
                        >
                            {sending ? 'Sending to Xero…' : 'Send to Xero as Draft'}
                        </Button>
                    </Stack>
                    <Typography color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'right', mt: 1 }}>
                        The entries are marked invoiced and a copy is saved to the client’s invoices.{' '}
                        <Link
                            component="button"
                            type="button"
                            onClick={back}
                            sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
                        >
                            Change the selection
                        </Link>
                    </Typography>
                </Paper>
            )}
        </Container>
    );
}
